import {FC, useState, useEffect} from 'react';
import {apiValidateDescription} from '@/services/ShredService';
import {ShredResponse, ValidateDescriptionRequest} from "@/@types/shred.ts";
import {Form, Formik} from "formik";
import {FormContainer, FormItem} from "@/components/ui/Form/index";
import * as Yup from "yup";
import Alert from "@/components/ui/Alert/index";
import Ckeditor5Field from "@/components/Ckeditor5Field";
import { ChevronRight, FileText, CheckCircle, Sparkles, ArrowLeft } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

interface StepTwoProps {
    onStepValidationResponse: (response: ShredResponse) => void;
    onStepError: (error: string) => void;
    shredId: string;
    shredName: string;
    shredDescription: string;
    model?: boolean;
    isAlreadyValidated?: boolean;
    serviceId?: string;
}

type CreateShredFormSchema = {
    shredDescription: string
}

const StepTwo: FC<StepTwoProps> = (props) => {
    const {
        shredId,
        shredName,
        shredDescription,
        onStepValidationResponse,
        onStepError,
        model: initialModel = false,
        isAlreadyValidated = false,
        serviceId,
    } = props

    console.log("🔍 StepTwo props:", { shredId, shredName, initialModel, isAlreadyValidated, serviceId });

    const [validationText, setValidationText] = useState("");
    const [improvedPrompt, setImprovedPrompt] = useState("");
    const [isModel, setIsModel] = useState(initialModel);
    const [isValidated, setIsValidated] = useState(isAlreadyValidated);
    
    const { projectId } = useParams();
    const navigate = useNavigate();
    
    // Get the previous page from breadcrumbs (same logic as Breadcrumb component)
    const getPreviousPage = () => {
        const stored = localStorage.getItem("breadcrumbs");
        if (stored) {
            const breadcrumbs = JSON.parse(stored);
            if (breadcrumbs.length >= 2) {
                return breadcrumbs[breadcrumbs.length - 2];
            }
        }
        return null;
    };

    console.log("🔍 StepTwo state:", { isModel, isValidated, projectId });
    
    // Add logging to track component lifecycle
    useEffect(() => {
        console.log("🔍 StepTwo mounted/updated");
        return () => {
            console.log("🔍 StepTwo unmounting");
        };
    });

    const validationSchema = Yup.object().shape({
        shredDescription: Yup.string()
            .test(
                'is-html-empty',
                'Please enter the shred description',
                value => {
                    if (!value) return false;
                    const strippedValue = value.replace(/<[^>]*>?/gm, '').trim();
                    return strippedValue.length > 0;
                }
            )
            .required('Please enter the shred description'),
    })

    const onDescriptionValidation = (
        values: CreateShredFormSchema,
        setSubmitting: (isSubmitting: boolean) => void
    ) => {
        console.log("onDescriptionValidation called with values:", values); // Debug log
        const {shredDescription} = values

        onStepError("");
        setSubmitting(true)

        console.log("Making API call to validate description"); // Debug log

        apiValidateDescription<ShredResponse, ValidateDescriptionRequest>(shredId, {shredDescription})
            .then((result) => {
                console.log("API response received:", result); // Debug log
                const {validated, validationText, improvedPrompt, model} = result.data.body;

                console.log("🔍 Setting isModel to:", model, "from API response");
                setIsModel(model || false);
                setIsValidated(validated);
                console.log("🔍 Setting isValidated to:", validated);

                // Set validation messages for display
                if (!validated) {
                    setValidationText(validationText || "");
                    setImprovedPrompt(improvedPrompt || "");
                } else {
                    // Clear any previous validation messages on success
                    setValidationText("");
                    setImprovedPrompt("");
                }

                // Always call the response handler - let parent handle the navigation
                onStepValidationResponse(result.data);
                
                // If this is a successfully validated model, we need to trigger a re-render
                // The parent will handle setting isModelValidated to true via the response handler
            })
            .catch((err) => {
                console.error("API error:", err); // Debug log
                onStepError(err?.response?.data?.body?.message || err?.response?.data?.message || "An error occurred while creating the shred")
            })
            .finally(() => {
                console.log("Setting submitting to false"); // Debug log
                setSubmitting(false)
            })
    }

    return (
        <div className="space-y-4 md:space-y-6">
            {/* Validation Messages */}
            {validationText && (
                <Alert className="animate-fadeIn" type="info">
                    <div className="flex items-start gap-2 md:gap-3">
                        <div className="p-1 bg-blue-100 rounded-full flex-shrink-0">
                            <FileText className="w-3 h-3 md:w-4 md:h-4 text-blue-600" />
                        </div>
                        <div>
                            <h4 className="font-medium text-blue-800 mb-1 text-sm md:text-base">AI Validation Feedback</h4>
                            <p dangerouslySetInnerHTML={{__html: validationText}} className="text-blue-700 text-xs md:text-sm" />
                        </div>
                    </div>
                </Alert>
            )}

            {improvedPrompt && (
                <Alert className="animate-fadeIn" type="success">
                    <div className="flex items-start gap-2 md:gap-3">
                        <div className="p-1 bg-green-100 rounded-full flex-shrink-0">
                            <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-green-600" />
                        </div>
                        <div>
                            <h4 className="font-medium text-green-800 mb-1 text-sm md:text-base">AI Enhanced Suggestion</h4>
                            <p dangerouslySetInnerHTML={{__html: improvedPrompt}} className="text-green-700 text-xs md:text-sm" />
                        </div>
                    </div>
                </Alert>
            )}

            {/* Model Validation Success Message - Only show after successful validation */}
            {isValidated && isModel && (
                <Alert className="animate-fadeIn" type="success">
                    <div className="flex items-start gap-2 md:gap-3">
                        <div className="p-1 bg-green-100 rounded-full flex-shrink-0">
                            <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-600" />
                        </div>
                        <div>
                            <h4 className="font-medium text-green-800 mb-1 text-sm md:text-base">Model Validated Successfully!</h4>
                            <p className="text-green-700 text-xs md:text-sm">
                                Your data model has been validated and saved. You can now return to the shreds list to generate feature shreds based on this model.
                            </p>
                        </div>
                    </div>
                </Alert>
            )}

            {/* Back to Shreds List Button - Always show for model shreds */}
            {isModel && (
                    <div className="flex justify-center mb-4 md:mb-6">
                        <button
                            onClick={() => {
                                const previousPage = getPreviousPage();
                                if (previousPage) {
                                    console.log("🔍 Back button clicked, navigating to breadcrumb:", previousPage.href);
                                    navigate(previousPage.href);
                                } else {
                                    // Fallback to manual construction
                                    const fallbackUrl = serviceId 
                                        ? `/architecture/project/${projectId}/services/${serviceId}`
                                        : `/architecture/project/${projectId}/services`;
                                    console.log("🔍 Back button clicked, fallback navigation to:", fallbackUrl);
                                    navigate(fallbackUrl);
                                }
                            }}
                            className="inline-flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-sm md:text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-95"
                        >
                            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
                            <span>Back to Shreds List</span>
                        </button>
                    </div>
            )}

            {/* Show validated description for already validated models */}
            {isValidated && isModel && (
                <div className="bg-white rounded-xl border border-green-200 shadow-sm mb-4 md:mb-6">
                    <div className="p-4 md:p-6">
                        <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Validated Model Description
                        </h3>
                        <div className="bg-green-50 border border-green-200 p-3 md:p-4 rounded-lg">
                            <div className="text-gray-700 font-medium text-sm md:text-base">
                                <strong>Name:</strong> {shredName}
                            </div>
                            <div className="text-gray-700 text-sm md:text-base mt-2">
                                <strong>Description:</strong>
                                <div 
                                    className="mt-1 prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{__html: shredDescription}} 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Form - Only show if not already validated */}
            {!isValidated && (
            <Formik
                initialValues={{shredDescription: shredDescription || ''}}
                validationSchema={validationSchema}
                onSubmit={(values, {setSubmitting}) => {
                    console.log("Form submitted with values:", values); // Debug log
                    onDescriptionValidation(values, setSubmitting)
                }}
            >
                {({isSubmitting, values, errors, touched}) => {
                    console.log("Formik render - isSubmitting:", isSubmitting, "values:", values, "errors:", errors); // Debug log

                    return (
                        <Form>
                            {/* Progress Navigation Block */}
                            <div className="bg-gradient-to-r from-[#6F6AF8]/10 to-[#8B87FF]/10 border border-[#6F6AF8]/20 rounded-xl p-4 md:p-6 shadow-sm mb-6">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0">
                                    {/* Left Side - Information */}
                                    <div className="flex-1 md:pr-8">
                                        <div className="flex items-center gap-3 mb-3 md:mb-3">
                                            <div className="p-1.5 md:p-2 bg-[#6F6AF8]/10 rounded-lg">
                                                <FileText className="w-4 h-4 md:w-5 md:h-5 text-[#6F6AF8]" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                                                    Describe Your Shred
                                                </h3>
                                                <p className="text-xs md:text-sm text-gray-600">Step 2 of 5</p>
                                            </div>
                                        </div>

                                        <div className="space-y-2 text-xs md:text-sm text-gray-700">
                                            <p className="flex items-start gap-2">
                                                <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                                Review or refine your shred description
                                            </p>
                                            <p className="flex items-start gap-2">
                                                <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                                Don't worry about input/output details yet, focus on the business logic
                                            </p>
                                            <p className="flex items-start gap-2">
                                                <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                                You will have another chance to update this at step 4
                                            </p>
                                        </div>
                                    </div>

                                    {/* Right Side - AI Action Button */}
                                    <div data-tour="validate-model" className="flex-shrink-0 w-full md:w-auto">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            onClick={() => console.log("Button clicked!")} // Debug log
                                            className={`
                                                relative overflow-hidden group w-full md:w-auto
                                                px-4 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl font-bold text-sm md:text-lg
                                                bg-gradient-to-r from-[#6F6AF8] via-[#8B87FF] to-[#A855F7]
                                                hover:from-[#5A56E8] hover:via-[#7C78F5] hover:to-[#9333EA]
                                                text-white shadow-lg md:shadow-2xl hover:shadow-xl md:hover:shadow-3xl
                                                transform hover:scale-105 active:scale-95
                                                transition-all duration-300 ease-out
                                                border-2 border-white/20
                                                ${isSubmitting ? 'opacity-90' : ''}
                                            `}
                                        >
                                            {/* Animated background gradient */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-opacity duration-500"></div>

                                            {/* Sparkle effects */}
                                            <div className="absolute top-1 left-1 w-2 h-2 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 animate-ping"></div>
                                            <div className="absolute bottom-1 right-1 w-1.5 h-1.5 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 animate-ping animation-delay-200"></div>
                                            <div className="absolute top-1/2 left-1/4 w-1 h-1 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 animate-ping animation-delay-400"></div>

                                            {/* Button content */}
                                            <div className="relative flex items-center justify-center gap-2 md:gap-3">
                                                {isSubmitting ? (
                                                    <>
                                                        {/* AI Loading Animation */}
                                                        <div className="flex items-center gap-1 md:gap-2">
                                                            <div className="w-4 h-4 md:w-5 md:h-5 relative">
                                                                <div className="absolute inset-0 border-2 border-white/30 rounded-full"></div>
                                                                <div className="absolute inset-0 border-2 border-t-white border-r-white/70 border-b-white/40 border-l-white/10 rounded-full animate-spin"></div>
                                                            </div>
                                                            <Sparkles className="w-4 h-4 md:w-5 md:h-5 animate-pulse" />
                                                        </div>
                                                        <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                                                            AI Validating...
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="flex items-center gap-1">
                                                            <Sparkles className="w-3 h-3 md:w-4 md:h-4 group-hover:animate-bounce animation-delay-100" />
                                                        </div>
                                                        <span className="bg-gradient-to-r from-white to-white/90 bg-clip-text">
                                                            {isModel ? 'Validate & Save' : 'Validate & Continue'}
                                                        </span>
                                                        <ChevronRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform duration-200" />
                                                    </>
                                                )}
                                            </div>

                                            {/* Glow effect */}
                                            <div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-r from-[#6F6AF8] to-[#A855F7] opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-300"></div>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Form Section */}
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                                <div className="p-4 md:p-6">
                                    <FormContainer>
                                        <FormItem
                                            label="Shred Name"
                                            labelClass="text-base md:text-lg text-gray-800 font-medium mb-2"
                                            htmlFor='shredName'
                                        >
                                            <div className="border border-gray-200 p-3 md:p-4 flex items-center rounded-lg w-full whitespace-pre-wrap bg-gray-50/50 text-gray-700 font-medium text-sm md:text-base">
                                                {shredName}
                                            </div>
                                        </FormItem>

                                        <FormItem
                                            label="Shred Description"
                                            labelClass="text-base md:text-lg text-gray-800 font-medium mb-2"
                                            invalid={(errors.shredDescription && touched.shredDescription) as boolean}
                                            asterisk
                                            htmlFor='shredDescription'
                                            errorMessage={errors.shredDescription}
                                        >
                                            <div className="editor-container">
                                                <Ckeditor5Field
                                                    name="shredDescription"
                                                    className="w-full min-h-[150px] md:min-h-[200px]"
                                                    placeholder="Describe what your shred should do, including inputs, outputs, business logic, and any specific requirements..."
                                                />
                                            </div>
                                        </FormItem>
                                    </FormContainer>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
            )}
        </div>
    );
};

export default StepTwo;